import { useTranslation } from 'react-i18next';
import { popularityScore, Task } from '@quizdb/common';

/** Props for `FeedbackCard`. */
export interface FeedbackCardProps {
  task: Task;
}

/** Whether a task carries any feedback at all (FBK-D1): any of its four feedback fields is set. */
export const hasFeedback = (task: Task): boolean =>
  task.usedCount !== undefined ||
  task.declinedCount !== undefined ||
  task.declineReasons !== undefined ||
  task.feedbackAt !== undefined;

/**
 * A popularity score as two decimals, a negative value with `−` (U+2212) rather than a hyphen,
 * and a value that rounds to zero as `0.00` (never `-0.00`).
 */
export const formatPopularityScore = (score: number): string => {
  const fixed = score.toFixed(2);
  if (Number(fixed) === 0) {
    return '0.00';
  }
  return fixed.startsWith('-') ? `−${fixed.slice(1)}` : fixed;
};

/**
 * A task's feedback, its counts, popularity score and decline reasons (FBK-D1, QZ-D153). `null`
 * for a task with no feedback at all.
 */
export const FeedbackCard = ({ task }: FeedbackCardProps) => {
  const { t } = useTranslation();

  if (!hasFeedback(task)) {
    return null;
  }

  const reasons = task.declineReasons ?? [];

  return (
    <div className="qz-card mt-0">
      <div className="qz-card-header">
        <h3>{t('task.page.feedback.title')}</h3>
      </div>
      <div className="qz-card-body">
        <dl className="qz-kv">
          <dt>{t('task.page.feedback.used')}</dt>
          <dd className="qz-mono">{task.usedCount ?? 0}</dd>
          <dt>{t('task.page.feedback.declined')}</dt>
          <dd className="qz-mono">{task.declinedCount ?? 0}</dd>
          <dt>{t('task.page.feedback.score')}</dt>
          <dd className="qz-mono">{formatPopularityScore(popularityScore(task))}</dd>
        </dl>
        <h2 className="qz-section-title">{t('task.page.feedback.reasons')}</h2>
        {reasons.length > 0 ? (
          <ul className="list-unstyled mb-0">
            {reasons.map((reason, index) => (
              <li key={index}>{reason}</li>
            ))}
          </ul>
        ) : (
          <p className="qz-small qz-muted mb-0">{t('task.page.feedback.noReasons')}</p>
        )}
      </div>
    </div>
  );
};

export default FeedbackCard;
